import React, { FC, ReactElement } from 'react';
import useLoadable from '@app/hooks/loadable/useLoadable';
import validationSystemServices from '@mortee/services/validationSystemServices';
import {
  evidenceTypeText,
  enrichmentEvidenceTypes,
  SupplierValidationRecord,
  transformSupplierValidationVerificationRecordEvidence,
  SupplierValidationVerificationRecordEvidence,
} from '@mortee/domain/validationSystem';
import { Regular16TransparentBlack900 } from '@app/components/Text';
import Button from '@app/components/Button';
import DataGrid, { DataGridRow } from '@app/components/DataGrid';
import styled from '@emotion/styled';
import { showYesNoModal } from '@app/components/Modal';
import { shoot } from '@app/utils/messageLauncher';
import useInfraStores from '@app/hooks/useInfraStores';
import { extractLogErrorIdFromError, HttpStatus, isRequestError } from '@app/libs/request';
import { formattedDateOptionsDDMMYYYY } from '@app/utils/timeUtils';
import { TransparentCard } from '@mortee/routes/validationSystem/editValidtionRecord/EditValidationRecordStyles';
import LoadableTransitionLoader from '@app/components/LoadableTransitionLoader';

interface Props {
  record: SupplierValidationRecord;
  disabled: boolean;
  onRecordRefreshNeeded?(): void;
}

const SupplierValidationRecordEvidence: FC<Props> = ({ record, disabled, onRecordRefreshNeeded }) => {
  const { localeStore } = useInfraStores();
  const { staticId, versionId } = record;

  const doesContainEmptyEnrichmentEvidence = (evidences: SupplierValidationVerificationRecordEvidence[]): boolean => {
    return evidences.some((evidence) => evidence.taxId === null);
  };

  // Helper function to check if country code is US or Europe
  const isUSOrEurope = (countryCode: string | null | undefined): boolean => {
    if (!countryCode) return false;

    // US country code
    if (countryCode === 'US') return true;

    // European country codes
    const europeanCountries = [
      // Western Europe
      'GB',
      'DE',
      'FR',
      'IT',
      'ES',
      'NL',
      'BE',
      'AT',
      'PT',
      'IE',
      'LU',
      'CH',
      // Nordic Countries
      'FI',
      'SE',
      'DK',
      'NO',
      'IS',
      // Eastern Europe
      'PL',
      'CZ',
      'SK',
      'HU',
      'SI',
      'HR',
      'BG',
      'RO',
      'EE',
      'LV',
      'LT',
      // Southern Europe
      'GR',
      'CY',
      'MT',
      // Southeastern Europe
      'AL',
      'BA',
      'ME',
      'MK',
      'RS',
      'XK',
      // Eastern Europe (non-EU)
      'BY',
      'UA',
      'MD',
      // Other
      'MC',
      'SM',
      'VA',
      'AD',
      'LI',
    ];

    return europeanCountries.includes(countryCode);
  };

  const [evidences, reloadEvidence] = useLoadable(async () => {
    try {
      const serverResponse = await validationSystemServices.getVerificationRecordEvidence(staticId);
      const transformedEvidence = serverResponse.map((evidence) =>
        transformSupplierValidationVerificationRecordEvidence(evidence),
      );

      // Split evidences into two categories
      const enrichmentEvidence = transformedEvidence.filter(
        (evidence) => evidence.type && enrichmentEvidenceTypes.includes(evidence.type),
      );

      const validationEvidence = transformedEvidence.filter(
        (evidence) => !evidence.type || !enrichmentEvidenceTypes.includes(evidence.type),
      );

      // Sort each category by effective date (max to min), handling cases where effectiveDate might be null/undefined
      const sortByEffectiveDate = (
        evidenceA: SupplierValidationVerificationRecordEvidence,
        evidenceB: SupplierValidationVerificationRecordEvidence,
      ): number => {
        if (!evidenceA.effectiveDate && !evidenceB.effectiveDate) return 0;
        if (!evidenceA.effectiveDate) return 1; // Put items without dates at the end
        if (!evidenceB.effectiveDate) return -1;
        return new Date(evidenceB.effectiveDate).getTime() - new Date(evidenceA.effectiveDate).getTime();
      };

      enrichmentEvidence.sort(sortByEffectiveDate);
      validationEvidence.sort(sortByEffectiveDate);

      return {
        validationEvidence,
        enrichmentEvidence,
      };
    } catch (error) {
      // Handle 404 EVIDENCE_NOT_FOUND as empty array
      if (isRequestError(error) && error.code === HttpStatus.notFound) {
        return {
          validationEvidence: [],
          enrichmentEvidence: [],
        };
      }
      // Re-throw other errors to maintain existing rejection logic
      throw error;
    }
  }, [staticId, versionId]);

  const onDeleteEvidence = (evidenceId: string): void => {
    showYesNoModal(
      'Are you sure you want to delete this record evidence?',
      async (): Promise<void> => {
        const closeLoadingMessage = shoot({ type: 'loading', closeable: true, duration: null }, 'Deleting...');
        try {
          await validationSystemServices.deleteVerificationRecordEvidence(staticId, evidenceId);
          await reloadEvidence();
          closeLoadingMessage();
          shoot({ type: 'success', closeable: true }, `The record's evidence have been deleted`);
        } catch {
          closeLoadingMessage();
        }
      },
      'Delete Evidence',
    );
  };

  const onEnrichEvidence = async (): Promise<void> => {
    const closeLoadingMessage = shoot({ type: 'loading', closeable: true, duration: null }, 'Enriching evidence...');
    try {
      await validationSystemServices.enrichVerificationRecordEvidences(staticId);
      await reloadEvidence();
      closeLoadingMessage();
      if (onRecordRefreshNeeded) {
        onRecordRefreshNeeded();
      }
      shoot({ type: 'success', closeable: true }, `Evidence enrichment completed successfully`);
    } catch (error) {
      closeLoadingMessage();
      shoot({ type: 'error', closeable: true }, `Failed to enrich evidence`);
    }
  };

  const onRefreshEnrichmentEvidence = async (): Promise<void> => {
    const closeLoadingMessage = shoot({ type: 'loading', closeable: true, duration: null }, 'Retrying evidence enrichment...');
    try {
      await validationSystemServices.refreshEnrichmentEvidences(staticId);
      await reloadEvidence();
      closeLoadingMessage();
      if (onRecordRefreshNeeded) {
        onRecordRefreshNeeded();
      }
      shoot({ type: 'success', closeable: true }, `Evidence enrichment completed successfully`);
    } catch (error) {
      closeLoadingMessage();
      shoot({ type: 'error', closeable: true }, `Failed to enrich evidence`);
    }
  };

  if (evidences.isRejected()) {
    const error = evidences.stateMetadata?.error;

    const displayMessage =
      isRequestError(error) && error.code === HttpStatus.notFound
        ? 'No evidence were found for this record'
        : `Error loading evidence data, refer to error code: ${extractLogErrorIdFromError(error)}`;

    return (
      <TransparentCard>
        <div data-testid='no-evidence-found'>{displayMessage}</div>
      </TransparentCard>
    );
  }

  return (
    <LoadableTransitionLoader loadable={evidences}>
      {({ validationEvidence, enrichmentEvidence }): ReactElement => {
        const totalEvidenceCount = validationEvidence.length + enrichmentEvidence.length;

        if (totalEvidenceCount === 0) {
          return (
            <EvidenceContainer>
              <TransparentCard>
                <div data-testid='no-evidence-found'>No evidence found for this record</div>
              </TransparentCard>
              {enrichmentEvidence.length === 0 &&
                record.supplierRegistrationProcess &&
                isUSOrEurope(record.supplierRegistrationProcess.countryCode) && (
                  <EnrichButtonContainer>
                    <Button appearance='full' id='btn-enrich-validation-record-evidence' onClick={onEnrichEvidence}>
                      Enrich validation record evidence
                    </Button>
                  </EnrichButtonContainer>
                )}
            </EvidenceContainer>
          );
        }

        return (
          <EvidenceContainer>
            {validationEvidence.length > 0 && (
              <>
                <SectionHeader>
                  <Regular16TransparentBlack900.div>
                    Validation Evidence ({validationEvidence.length})
                  </Regular16TransparentBlack900.div>
                </SectionHeader>
                {validationEvidence.map((evidenceData, index) => (
                  <EvidenceCard
                    key={evidenceData.id || `validation-${index}`}
                    evidenceData={evidenceData}
                    index={index}
                    categoryPrefix='validation'
                    disabled={disabled}
                    onDelete={onDeleteEvidence}
                    localeStore={localeStore}
                  />
                ))}
              </>
            )}

            {enrichmentEvidence.length > 0 && (
              <>
                <SectionHeader>
                  <Regular16TransparentBlack900.div>
                    Enrichment Evidence ({enrichmentEvidence.length})
                  </Regular16TransparentBlack900.div>
                </SectionHeader>
                {enrichmentEvidence.map((evidenceData, index) => (
                  <EvidenceCard
                    key={evidenceData.id || `enrichment-${index}`}
                    evidenceData={evidenceData}
                    index={index}
                    categoryPrefix='enrichment'
                    disabled={disabled}
                    onDelete={onDeleteEvidence}
                    localeStore={localeStore}
                  />
                ))}
              </>
            )}
            {enrichmentEvidence.length === 0 &&
              record.supplierRegistrationProcess &&
              isUSOrEurope(record.supplierRegistrationProcess.countryCode) && (
                <EnrichButtonContainer>
                  <Button appearance='full' id='btn-enrich-validation-record-evidence' onClick={onEnrichEvidence}>
                    Enrich validation record evidence
                  </Button>
                </EnrichButtonContainer>
              )}
            {record.supplierRegistrationProcess &&
              isUSOrEurope(record.supplierRegistrationProcess.countryCode) &&
              doesContainEmptyEnrichmentEvidence(record.enrichmentEvidence) && (
                <EnrichButtonContainer>
                  <Button
                    appearance='full'
                    id='btn-refresh-enrichment-evidence'
                    onClick={onRefreshEnrichmentEvidence}
                    disabled={record.enrichmentEvidenceRefreshesDone >= 3}
                  >
                    Retry failed enrichment evidence {`(${record.enrichmentEvidenceRefreshesDone}/3)`}
                  </Button>
                </EnrichButtonContainer>
              )}
          </EvidenceContainer>
        );
      }}
    </LoadableTransitionLoader>
  );
};

export default SupplierValidationRecordEvidence;

const EvidenceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TitleLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Separator = styled.hr`
  border-top: solid 1px var(--transparent-black-200);
  margin: 14px 0;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  align-content: start;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 20px;
  margin-top: 25px;
`;

const SectionHeader = styled.div`
  margin-top: 20px;
  margin-bottom: 10px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const EnrichButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 15px;
`;

interface EvidenceCardProps {
  evidenceData: SupplierValidationVerificationRecordEvidence;
  index: number;
  categoryPrefix: string;
  disabled: boolean;
  onDelete: (evidenceId: string) => void;
  localeStore: any; // You might want to type this more specifically
}

const EvidenceCard: FC<EvidenceCardProps> = ({
  evidenceData,
  index,
  categoryPrefix,
  disabled,
  onDelete,
  localeStore,
}): ReactElement => {
  // Check if this is enrichment evidence with no results (taxId is null)
  const isEnrichmentWithNoResults = categoryPrefix === 'enrichment' && !evidenceData.taxId;

  return (
    <TransparentCard key={evidenceData.id || `${categoryPrefix}-${index}`}>
      <TitleLine>
        <div>
          <Regular16TransparentBlack900.div>{evidenceData.type}</Regular16TransparentBlack900.div>
        </div>
      </TitleLine>
      <Separator />

      {isEnrichmentWithNoResults ? (
        <div style={{ padding: '15px', textAlign: 'center' }}>
          API called at{' '}
          {evidenceData.effectiveDate
            ? localeStore.formatDateTime(evidenceData.effectiveDate, formattedDateOptionsDDMMYYYY)
            : 'unknown date'}
          , no results found
        </div>
      ) : (
        <TwoColumnGrid>
          <DataGrid>
            {evidenceData.type && (
              <DataGridRow title='Type:' valueDataTest={`${categoryPrefix}-evidence-type-${index}`}>
                {evidenceTypeText[evidenceData.type]}
              </DataGridRow>
            )}
            {evidenceData.data.payeeName && (
              <DataGridRow title='Payee Name:' valueDataTest={`${categoryPrefix}-evidence-payee-name-${index}`}>
                {evidenceData.data.payeeName}
              </DataGridRow>
            )}
            {evidenceData.data.formerNames && evidenceData.data.formerNames.length > 0 && (
              <DataGridRow title='Former Name(s):' valueDataTest={`${categoryPrefix}-evidence-former-names-${index}`}>
                {evidenceData.data.formerNames.join(', ')}
              </DataGridRow>
            )}
            {evidenceData.data.alternativeNames && evidenceData.data.alternativeNames.length > 0 && (
              <DataGridRow title='Alternative Name(s):' valueDataTest={`${categoryPrefix}-evidence-alternative-names-${index}`}>
                {evidenceData.data.alternativeNames.join(', ')}
              </DataGridRow>
            )}
            {evidenceData.data.address && (
              <DataGridRow title='Address:' valueDataTest={`${categoryPrefix}-evidence-address-${index}`}>
                {evidenceData.data.address}
              </DataGridRow>
            )}
            {evidenceData.taxId && (
              <DataGridRow title='Tax Id:' valueDataTest={`${categoryPrefix}-evidence-tax-id-${index}`}>
                {evidenceData.taxId}
              </DataGridRow>
            )}
            {evidenceData.taxIdType && (
              <DataGridRow title='Tax Id Type:' valueDataTest={`${categoryPrefix}-evidence-tax-id-type-${index}`}>
                {evidenceData.taxIdType}
              </DataGridRow>
            )}
            {evidenceData.taxIdCountryCode && (
              <DataGridRow title='Tax Id Country Code:' valueDataTest={`${categoryPrefix}-evidence-tax-id-country-code-${index}`}>
                {evidenceData.taxIdCountryCode}
              </DataGridRow>
            )}
            {evidenceData.additionalTaxId && (
              <DataGridRow title='Additional LEI:' valueDataTest={`${categoryPrefix}-evidence-additional-tax-id-${index}`}>
                {evidenceData.additionalTaxId}
              </DataGridRow>
            )}
            {evidenceData.additionalTaxIdType && (
              <DataGridRow
                title='Additional LEI Type:'
                valueDataTest={`${categoryPrefix}-evidence-additional-tax-id-type-${index}`}
              >
                {evidenceData.additionalTaxIdType}
              </DataGridRow>
            )}
            {evidenceData.result && (
              <DataGridRow title='Result:' valueDataTest={`${categoryPrefix}-evidence-result-${index}`}>
                {evidenceData.result}
              </DataGridRow>
            )}
            {evidenceData.data.comment && (
              <DataGridRow title='Comment:' valueDataTest={`${categoryPrefix}-evidence-comment-${index}`}>
                {evidenceData.data.comment}
              </DataGridRow>
            )}
            {evidenceData.evidenceRecordId && (
              <DataGridRow title='Evidence Record Id:' valueDataTest={`${categoryPrefix}-evidence-record-id-${index}`}>
                {evidenceData.evidenceRecordId}
              </DataGridRow>
            )}
            {evidenceData.customerId && (
              <DataGridRow title='Customer id:' valueDataTest={`${categoryPrefix}-evidence-customer-id-${index}`}>
                {evidenceData.customerId}
              </DataGridRow>
            )}
          </DataGrid>
          <DataGrid>
            {evidenceData.data.bankName && (
              <DataGridRow title='Bank Name:' valueDataTest={`${categoryPrefix}-evidence-bank-name-${index}`}>
                {evidenceData.data.bankName}
              </DataGridRow>
            )}
            {evidenceData.swiftCode && (
              <DataGridRow title='SWIFT:' valueDataTest={`${categoryPrefix}-evidence-swift-${index}`}>
                {evidenceData.swiftCode}
              </DataGridRow>
            )}
            {evidenceData.iban && (
              <DataGridRow title='IBAN:' valueDataTest={`${categoryPrefix}-evidence-iban-${index}`}>
                {evidenceData.iban}
              </DataGridRow>
            )}
            {evidenceData.bankCode && (
              <DataGridRow title='Bank Code:' valueDataTest={`${categoryPrefix}-evidence-bank-code-${index}`}>
                {evidenceData.bankCode}
              </DataGridRow>
            )}
            {evidenceData.branchCode && (
              <DataGridRow title='Branch Code:' valueDataTest={`${categoryPrefix}-evidence-branch-code-${index}`}>
                {evidenceData.branchCode}
              </DataGridRow>
            )}
            {evidenceData.accountNumber && (
              <DataGridRow title='Account Number:' valueDataTest={`${categoryPrefix}-evidence-account-number-${index}`}>
                {evidenceData.accountNumber}
              </DataGridRow>
            )}
            {evidenceData.currency && (
              <DataGridRow title='Currency:' valueDataTest={`${categoryPrefix}-evidence-currency-${index}`}>
                {evidenceData.currency}
              </DataGridRow>
            )}
            {evidenceData.amount && (
              <DataGridRow title='Amount:' valueDataTest={`${categoryPrefix}-evidence-amount-${index}`}>
                {evidenceData.amount}
              </DataGridRow>
            )}
            {evidenceData.effectiveDate && (
              <DataGridRow title='Effective Date:' valueDataTest={`${categoryPrefix}-evidence-effective-date-${index}`}>
                {localeStore.formatDateTime(evidenceData.effectiveDate, formattedDateOptionsDDMMYYYY)}
              </DataGridRow>
            )}
          </DataGrid>
        </TwoColumnGrid>
      )}

      <ButtonsContainer>
        <Button
          appearance='full'
          id={`btn-edit-validation-process-delete-${categoryPrefix}-evidence-${index}`}
          onClick={(): void => onDelete(evidenceData.id)}
          disabled={disabled || !evidenceData.isDeletable}
        >
          DELETE EVIDENCE
        </Button>
      </ButtonsContainer>
    </TransparentCard>
  );
};
